<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useFunctionGetter } from 'dashboard/composables/store';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessengerTemplatesHelp from './components/MessengerTemplatesHelp.vue';
import {
  COMPONENT_TYPES,
  findComponentByType,
} from 'dashboard/helper/templateHelper';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const isSyncing = ref(false);

const inboxId = computed(() => props.inbox.id);

const filteredTemplates = useFunctionGetter(
  'inboxes/getFilteredInboxMessageTemplates',
  inboxId
);

const isMetaChannel = computed(
  () =>
    props.inbox.channel_type === INBOX_TYPES.FB ||
    props.inbox.channel_type === INBOX_TYPES.INSTAGRAM
);

const syncTitleKey = computed(() =>
  isMetaChannel.value
    ? 'INBOX_MGMT.MESSAGE_TEMPLATES_TAB.META_SYNC_TITLE'
    : 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE'
);

const syncSubtitleKey = computed(() =>
  isMetaChannel.value
    ? 'INBOX_MGMT.MESSAGE_TEMPLATES_TAB.META_SYNC_SUBTITLE'
    : 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER'
);

function bodyPreview(template) {
  return findComponentByType(template, COMPONENT_TYPES.BODY)?.text || '';
}

async function onSyncTemplates() {
  isSyncing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', inboxId.value);
    await store.dispatch('inboxes/get');
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isSyncing.value = false;
  }
}
</script>

<template>
  <div class="mx-6 flex max-w-4xl flex-col gap-8">
    <SettingsFieldSection
      :label="$t(syncTitleKey)"
      :help-text="$t(syncSubtitleKey)"
    >
      <NextButton
        :is-loading="isSyncing"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON')"
        @click="onSyncTemplates"
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.MESSAGE_TEMPLATES_TAB.LIST_TITLE')"
      :help-text="$t('INBOX_MGMT.MESSAGE_TEMPLATES_TAB.LIST_SUBTITLE')"
    >
      <div
        class="max-h-[24rem] overflow-y-auto rounded-lg border border-n-weak bg-n-alpha-black2 p-3"
        role="list"
        :aria-label="$t('INBOX_MGMT.MESSAGE_TEMPLATES_TAB.LIST_ARIA')"
      >
        <template v-if="filteredTemplates.length">
          <div
            v-for="(template, index) in filteredTemplates"
            :key="
              template.id || `${template.name}-${template.language}-${index}`
            "
            role="listitem"
            class="border-b border-n-weak py-3 last:border-b-0 last:pb-0"
          >
            <div
              class="mb-1 flex flex-wrap items-baseline justify-between gap-2"
            >
              <span class="text-sm font-semibold text-n-slate-12">{{
                template.name
              }}</span>
              <span class="text-xs text-n-slate-11">
                {{ $t('INBOX_MGMT.MESSAGE_TEMPLATES_TAB.LANGUAGE') }}:
                {{ template.language }}
              </span>
            </div>
            <p
              v-if="bodyPreview(template)"
              class="mb-0 font-mono text-xs leading-relaxed text-n-slate-11 line-clamp-4"
            >
              {{ bodyPreview(template) }}
            </p>
            <p
              v-if="template.category"
              class="mb-0 mt-2 text-xs text-n-slate-11"
            >
              {{ $t('INBOX_MGMT.MESSAGE_TEMPLATES_TAB.CATEGORY') }}:
              {{ template.category }}
            </p>
          </div>
        </template>
        <p v-else class="mb-0 py-6 text-center text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.MESSAGE_TEMPLATES_TAB.EMPTY') }}
        </p>
      </div>
    </SettingsFieldSection>

    <div v-if="!isMetaChannel" class="text-sm leading-relaxed text-n-slate-11">
      {{ $t('INBOX_MGMT.MESSAGE_TEMPLATES_TAB.WHATSAPP_NOTE') }}
    </div>

    <MessengerTemplatesHelp v-if="isMetaChannel" class="w-full" />
  </div>
</template>
